import { AudienceByWorkspaceResponse } from '@/models/AudienceByWorkspace';

/** Unwrap GET /users/members payloads: raw array, { members, total?, limit?, offset? }, or legacy { data: { members } }. */
export function normalizeAudienceResponse(raw: unknown): AudienceByWorkspaceResponse {
  const r = raw as Record<string, unknown> | unknown[] | null | undefined;
  let list: unknown[] = [];

  if (Array.isArray(r)) {
    list = r;
  } else if (r && typeof r === 'object') {
    const o = r as Record<string, unknown>;
    let dataHandled = false;
    // Prefer nested `data` when present (legacy) over top-level `members`.
    if (o.data !== undefined) {
      const inner = o.data as Record<string, unknown> | unknown[];
      if (Array.isArray(inner)) {
        list = inner;
        dataHandled = true;
      } else if (inner && typeof inner === 'object') {
        const d = inner as Record<string, unknown>;
        if (Array.isArray(d.members)) {
          list = d.members as unknown[];
          dataHandled = true;
        } else if (Array.isArray(d.audience)) {
          list = d.audience as unknown[];
          dataHandled = true;
        }
      }
    }
    if (!dataHandled && Array.isArray(o.members)) {
      list = o.members as unknown[];
    } else if (!dataHandled && Array.isArray(o.audience)) {
      list = o.audience as unknown[];
    }
  }

  const members = list.map((member: any) => {
    const m = { ...member };
    // Shared Table row click checks entityType === 'Audience' (Dynamo shape). .NET returns plain members without it.
    if (m.entityType == null || m.entityType === '') {
      m.entityType = 'Audience';
    }
    if (m.churnRiskData != null) {
      try {
        m.parsedChurnRiskData =
          typeof m.churnRiskData === 'string' ? JSON.parse(m.churnRiskData) : m.churnRiskData;
      } catch {
        m.parsedChurnRiskData = null;
      }
    }
    if (m.active === undefined && typeof m.stripeActiveSubscriptions === 'number') {
      m.active = m.stripeActiveSubscriptions > 0;
    }
    if (m.subscribed === undefined && typeof m.stripeActiveSubscriptions === 'number') {
      m.subscribed = m.stripeActiveSubscriptions > 0;
    }
    return m;
  });

  const o = r && typeof r === 'object' && !Array.isArray(r) ? (r as Record<string, unknown>) : {};
  const innerData =
    o.data && typeof o.data === 'object' && !Array.isArray(o.data) ? (o.data as Record<string, unknown>) : null;

  const total =
    typeof innerData?.total === 'number'
      ? innerData.total
      : typeof (o as Record<string, unknown>).total === 'number'
        ? ((o as Record<string, unknown>).total as number)
        : undefined;
  const limit =
    typeof innerData?.limit === 'number'
      ? innerData.limit
      : typeof (o as Record<string, unknown>).limit === 'number'
        ? ((o as Record<string, unknown>).limit as number)
        : undefined;
  const offset =
    typeof innerData?.offset === 'number'
      ? innerData.offset
      : typeof (o as Record<string, unknown>).offset === 'number'
        ? ((o as Record<string, unknown>).offset as number)
        : undefined;

  return {
    members,
    audience: members,
    churnMetrics:
      (o.churnMetrics as AudienceByWorkspaceResponse['churnMetrics']) ??
      (innerData?.churnMetrics as AudienceByWorkspaceResponse['churnMetrics']),
    overview:
      (o.overview as AudienceByWorkspaceResponse['overview']) ??
      (innerData?.overview as AudienceByWorkspaceResponse['overview']),
    signals:
      (o.signals as AudienceByWorkspaceResponse['signals']) ??
      (innerData?.signals as AudienceByWorkspaceResponse['signals']),
    total,
    limit,
    offset,
  };
}
